/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IOrderSchedulerData } from './order-scheduler.data';
import { ExcludeDaysDay, IOrderSchedulerProps } from './order-scheduler.props.autogenerated';
import DatePicker from 'react-datepicker';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';

export interface IOrderSchedulerViewProps extends IOrderSchedulerProps<IOrderSchedulerData> {
    datePicker: React.ReactNode;
    saveButtonProps: INodeProps;
    fieldLabelProps: INodeProps;
    orderScheduleWrapperProps: INodeProps;
    staticDisplay: React.ReactNode;
    shouldDisplayStatic: boolean;
}
interface IOrderSchedulerState {
    date: Date | null;
    displayStatic: boolean;
}
/**
 *
 * OrderScheduler component
 * @extends {React.PureComponent<IOrderSchedulerProps<IOrderSchedulerData>>}
 */
class OrderScheduler extends React.PureComponent<IOrderSchedulerProps<IOrderSchedulerData>, IOrderSchedulerState> {
    private _fieldId = 'date-picker-id';
    constructor(props: IOrderSchedulerProps<IOrderSchedulerData>) {
        super(props);
        this.state = {
            date: null,
            displayStatic: false
        };
    }

    public componentDidMount() {
        const result = this.props.data.checkout?.result;
        if (result) {
            const date = result.checkoutCart.cart.RequestedDeliveryDate;
            if (date) {
                const dateObj = new Date(date);

                if (!this._checkIfDefaultDate(dateObj)) {
                    this.setState({ date: dateObj, displayStatic: true });
                }
            }
        }
    }

    private _checkIfDefaultDate = (date: Date) => date.getTime() === new Date(0).getTime();

    private _handleDateChange = (newDate: Date) => this.setState({ date: newDate });

    private _renderDatePicker = () => {
        const currentDateISO = Date.now();
        return (
            <DatePicker
                maxDate={this._getMaxDate(currentDateISO)}
                excludeDates={this._getExclusionDates(currentDateISO)}
                selected={this.state.date}
                onChange={this._handleDateChange}
                className={'eve-po-number__input'}
                id={this._fieldId}
            />
        );
    };

    private _getExclusionDates = (currentDateISO: number): Date[] => {
        const maxDate = this._getMaxDate(currentDateISO);
        const runningDate = new Date(currentDateISO);
        const {
            config: { invalidDays, minDate }
        } = this.props;
        const datesToExclude: Date[] = [];
        if (!invalidDays) {
            return datesToExclude;
        }

        const exclude: number[] = [];
        if (invalidDays.find(d => d.day === ExcludeDaysDay.sunday)) {
            exclude.push(0);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.monday)) {
            exclude.push(1);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.tuesday)) {
            exclude.push(2);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.wednesday)) {
            exclude.push(3);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.thursday)) {
            exclude.push(4);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.friday)) {
            exclude.push(5);
        }
        if (invalidDays.find(d => d.day === ExcludeDaysDay.saturday)) {
            exclude.push(6);
        }
        let i = 0;
        while (runningDate < maxDate) {
            if (minDate && i < minDate) {
                datesToExclude.push(new Date(runningDate));
                i++;
            } else {
                const day = runningDate.getDay();
                if (exclude.includes(day)) {
                    datesToExclude.push(new Date(runningDate));
                }
            }
            runningDate.setDate(runningDate.getDate() + 1);
        }
        return datesToExclude;
    };

    private _getMaxDate = (currentDateISO: number) => {
        const {
            config: { maxMonths }
        } = this.props;
        const date = new Date(currentDateISO);
        date.setMonth(date.getMonth() + (maxMonths || 12));
        return date;
    };

    private _saveOrderSchedule = () => {
        const {
            data: {
                checkout: { result }
            }
        } = this.props;

        if (result) {
            const cart = Object.assign({}, result.checkoutCart.cart);
            if (cart) {
                void result.checkoutCart
                    .updateCart({
                        newCartObject: { ...cart, RequestedDeliveryDate: this.state.date || new Date(0) }
                    })
                    .then(() => this.setState({ displayStatic: (this.state.date && !this._checkIfDefaultDate(this.state.date)) || false }));
            }
        }
    };

    private _renderStaticDisplay = (): React.ReactNode => (
        <React.Fragment>
            <p className='eve-order-schedule__static' key={'order-schedule-p'}>
                {this.props.resources.orderSchedulerLabel}: {this.state.date?.toLocaleDateString() || ''}
            </p>
            <a className='eve-po-number__edit' onClick={() => this.setState({ displayStatic: false })} key={'order-schedule-a'}>
                {this.props.resources.orderScheduleSetOrEditText}
            </a>
        </React.Fragment>
    );

    public render(): JSX.Element | null {
        const {
            resources: { saveButtonText }
        } = this.props;
        const viewProps: IOrderSchedulerViewProps = {
            ...this.props,
            datePicker: this._renderDatePicker(),
            saveButtonProps: {
                tag: 'button',
                type: 'button',
                onClick: this._saveOrderSchedule,
                className: classnames('msc-btn', 'eve-order-schedule__button'),
                children: saveButtonText
            },
            fieldLabelProps: {
                tag: 'label',
                for: this._fieldId,
                children: this.props.resources.orderSchedulerLabel,
                className: ''
            },
            orderScheduleWrapperProps: {
                tag: 'div',
                className: classnames(this.props.config.className, 'eve-order-schedule')
            },
            staticDisplay: this._renderStaticDisplay(),
            shouldDisplayStatic: this.state.displayStatic
        };
        return this.props.renderView(viewProps);
    }
}

export default OrderScheduler;
